import { ingredientList } from '../UiElements';

export class Recipe {
  constructor(recipeIngredients, recipeTitle, recipeDescription) {
    this.ingredients = recipeIngredients.map(
      (e) =>
        (e = new Ingredient(
          e.name,
          e.qty,
          e.type,
          e.hydration || null,
          e.units || null
        ))
    );
    this.recipeInfo = {
      description: recipeDescription,
      title: recipeTitle,
    };
  }

  // Callback functions to make it easier to use the recipe array
  // Get an ingredient object from the array using unique name
  getIngredientByName = (name) => {
    const objIndex = this.ingredients.findIndex(
      (el) => el.name.toLowerCase() === name.toLowerCase()
    );
    return this.ingredients[objIndex];
  };

  // Sum all ingredients of a certain type
  sumIngredientbyType = (type) => {
    let sum = 0;
    this.ingredients.forEach((el) => {
      if (el.type.toLowerCase() === type.toLowerCase()) sum += el.qty;
    });
    return sum;
  };

  // Get all the ingredients of a certain type
  getIngredientsByType = (type) => {
    let ingArr = this.ingredients.filter(
      (el) => el.type.toLowerCase() === type.toLowerCase()
    );
    // Sort ingredients by quantity to make them easier to display
    ingArr.sort((a, b) => b.qty - a.qty);
    return ingArr;
  };

  /**
   * Methods to modify the recipe array
   *
   */

  // delete an ingredient
  deleteIngredient = (ingName) => {
    this.ingredients = this.ingredients.filter((el) => el.name !== ingName);
  };

  // add new ingredient
  addIngredient = (name, qty, type, hydration) => {
    if (this.getIngredientByName(name)) name = `Additional ${name}`;
    const ingredient = new Ingredient(name, qty, type, hydration);
    this.ingredients.push(ingredient);
  };

  // Modify an existing ingredient
  changeIngredient = (ingName, propToChange, newValue) => {
    const ingredient = this.getIngredientByName(ingName);
    //debugger;
    if (ingredient) {
      ingredient[propToChange] = newValue;
    } else {
      console.log(
        `Could not modify ${ingName} because the element was not found in the recipe.`
      );
    }
  };
}

class Ingredient {
  constructor(name, qty, type, hydration = -1, units) {
    this.name = name;
    this.qty = qty;
    this.type = type.toLowerCase();
    this.addHydration(hydration);
    this.addUnits(units);
  }
  addHydration = (hydration) => {
    if (hydration > 0) this.hydration = hydration;
  };

  addUnits = (units) => {
    if (units) this.units = units;
  };

  changeUnits = (newUnits) => {
    switch (`${this.units} to ${newUnits}`) {
      case 'g to oz':
        this.qty *= 0.0352739619;
        this.units = 'oz';
        break;
      case 'oz to g':
        this.qty *= 28.34952;
        this.units = 'g';
        break;
      case 'g to g':
      case 'oz to oz':
        console.log('It looks like ${this.name} is already in ${newUnits}!');
        break;
      default:
        console.log(
          `I can only convert grams (g) to ounces (oz) or vice versa. Please use either 'g' or 'oz' as the argument.`
        );
    }
  };
}

export class Dough extends Recipe {
  constructor(ingredientList, recipeTitle, recipeDescription) {
    super(ingredientList, recipeTitle, recipeDescription);
    this.updateDoughInfo();
  }

  round = (number, digits = 1) => {
    switch (digits) {
      case 0:
        return Math.round(number);
      case 1:
        return Math.round(number * 10) / 10;
      case 2:
        return Math.round(number * 100) / 100;
    }
  };

  getDoughMass = (excludeMixIns = false) => {
    let total = 0;
    if (excludeMixIns === true) {
      this.ingredients.forEach((el) => {
        if (
          el.type === 'flour' ||
          el.type === 'liquid' ||
          el.type === 'preferment' ||
          el.type === 'salt'
        )
          total += el.qty;
      });
      return total;
    } else {
      this.ingredients.forEach((el) => (total += el.qty));
      return total;
    }
  };

  /**
   * Function to return flour and water amounts from
   * an hydration ratio. Useful for levain and for doughs.
   *
   * @param {number} mass
   * @param {number: percent as decimal} hydration
   */
  hydrationBreakdown = (mass, hydration) => {
    const flour = mass / (1 + hydration);
    const liquid = mass - flour;
    return {
      mass: mass,
      hydration: hydration,
      flour: flour,
      liquid: liquid,
    };
  };

  getEndHydration = () => {
    let totalFlour = 0;
    let totalLiquid = 0;

    // get the flour from levain
    const preferment = this.getIngredientsByType('preferment');
    // TODO - I'm only using the first pre-ferment that gets returned
    // it's possible that someone would use multiple preferments
    const prefermentObj = this.hydrationBreakdown(
      preferment[0].qty,
      preferment[0].hydration
    );
    totalFlour = prefermentObj.flour + this.sumIngredientbyType('flour');

    // add the water/liquids
    totalLiquid = this.sumIngredientbyType('liquid') + prefermentObj.liquid;

    // update the end hydration
    return totalLiquid / totalFlour;
  };

  getBakersHydration = () => {
    return (
      this.sumIngredientbyType('liquid') / this.sumIngredientbyType('flour')
    );
  };

  // calc props on all the indivdiual ingredients
  calcDoughPct = () => {
    const doughMass = this.getDoughMass();
    this.ingredients.forEach((el) => {
      el.doughPct = el.qty / doughMass;
    });
  };

  calcBPct = () => {
    const totalFlour = this.sumIngredientbyType('flour');
    this.ingredients.forEach((el) => {
      el.BPct = el.qty / totalFlour;
    });
  };

  // Call all the calc methods
  updateDoughInfo = () => {
    this.recipeInfo.doughMass = this.getDoughMass();
    this.recipeInfo.bakersHydration = this.getBakersHydration();
    this.recipeInfo.endHydration = this.getEndHydration();
    this.calcDoughPct();
    this.calcBPct();
  };

  /**
   * This section mutates the recipe
   *
   */

  /**
   * Change the number of loaves in the recipe
   * @param {*} newLoafNum
   */
  setLoafNum = (newLoafNum) => {
    const curLoafNum = this.recipeInfo.loafNum ? this.recipeInfo.loafNum : 1;
    if (newLoafNum > 0) {
      this.ingredients.forEach(
        (el) => (el.qty = el.qty * (newLoafNum / curLoafNum))
      );
      this.recipeInfo.loafNum = newLoafNum;
    } else {
      console.log(
        `Please pass a newLoafNum argument that is greater than zero when calling setLoafNum.`
      );
    }
  };

  /**
   * Change the mass of a single loaf
   * @param {*} newMass
   * @param {*} excludeMixIns
   */
  setLoafMass = (newMass, excludeMixIns) => {
    if (newMass > 0) {
      const curLoafMass = this.recipeInfo.loafMass
        ? this.recipeInfo.loafMass
        : this.getDoughMass(excludeMixIns);
      this.ingredients.forEach((el) => {
        el.qty = el.qty * (newMass / curLoafMass);
      });
      this.recipeInfo.loafMass = newMass;
    } else {
      console.log(
        `Please pass a newMass argument that is greater than zero when calling setLoafMass.`
      );
    }
  };

  changeBakersHydration = (newHydration) => {
    // this method only modifies the amount of liquid and flour in the recipe
    // first we get the current amounts of liquid and flour
    const curLiquid = this.sumIngredientbyType('liquid');
    const curFlour = this.sumIngredientbyType('flour');
    const ingredeintsToScale = curFlour + curLiquid;

    // Get the new flour and water totals, then use it to figure out how much to scale the current
    // water and flour ingredients
    const newIngredientRatio = this.hydrationBreakdown(
      ingredeintsToScale,
      newHydration
    );
    const liquidMultiplier = newIngredientRatio.liquid / curLiquid;
    const flourMultipler = newIngredientRatio.flour / curFlour;

    // scale flour and liquid ingredients up or down
    this.ingredients.forEach((el) => {
      if (el.type === 'flour') el.qty *= flourMultipler;
      if (el.type === 'liquid') el.qty *= liquidMultiplier;
    });
  };
}
